import { type FormikValues, useFormikContext } from "formik";
import lodashGet from "lodash/get";
import React, {
  type ChangeEvent,
  type ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import PillarTableColumnBoolean, {
  type PillarTableBooleanIconType,
} from "shared/components/pillar-table/columns/PillarTableColumnBoolean";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";

export type PillarTableColumnCheckboxProps<T = any> =
  PillarTableBaseColumnCellValuePathProps<T> &
    Omit<
      ComponentPropsWithoutRef<"input">,
      "type" | "onChange" | "checked" | "disabled" | "ref"
    > & {
      /**
       * @deprecated Use `PillarTableColumnBoolean` instead of checkbox
       */
      checkedIcon?: PillarTableBooleanIconType;
      /**
       * @deprecated Use `PillarTableColumnBoolean` instead of checkbox
       */
      uncheckedIcon?: PillarTableBooleanIconType;
      /**
       * @deprecated Use `PillarTableColumnBoolean` instead of checkbox
       */
      readonly?: boolean;
      /**
       * @deprecated Use `PillarTableColumnBoolean` instead of checkbox
       */
      rowValueProperty?: string;
      defaultCheckAll?: boolean;
    };

const PillarTableColumnCheckbox = <T extends object = any>({
  cellValuePath,
  className,
  defaultCheckAll = false,
  ...props
}: PillarTableColumnCheckboxProps<T>) => {
  // We expect this component to be used in a formik context
  const formik = useFormikContext<FormikValues>();
  const row = usePillarTableRowContext<T, any, any>();

  if (props.checkedIcon || props.uncheckedIcon) {
    return (
      <PillarTableColumnBoolean
        cellValuePath={cellValuePath}
        trueIcon={props.checkedIcon!}
        falseIcon={props.uncheckedIcon!}
      />
    );
  }

  // Get the value for this row/cell
  const initialValue = lodashGet(row?.rowValue, cellValuePath ?? "");
  // Get in[] and not-in[] arrays from formik values
  const inArray: any[] | undefined = formik.values["arrayIn"];
  const notInArray: any[] | undefined = formik.values["arrayNotIn"];

  const isExplicitlyIn = inArray?.includes(initialValue) ?? false;
  const isExplicitlyNotIn = notInArray?.includes(initialValue) ?? false;
  const hasInArray = Array.isArray(inArray) && inArray.length > 0;
  const hasNotInArray = Array.isArray(notInArray) && notInArray.length > 0;

  let checked = false;
  if (defaultCheckAll) {
    if (!inArray && !notInArray) {
      checked = !isExplicitlyNotIn;
    } else if (!hasInArray && !hasNotInArray) {
      checked = false;
    } else if (hasInArray && !hasNotInArray) {
      checked = isExplicitlyIn;
    } else if (!hasInArray && hasNotInArray) {
      checked = !isExplicitlyNotIn;
    } else {
      checked = isExplicitlyIn && !isExplicitlyNotIn;
    }
  } else if (inArray?.length && !notInArray?.length) {
    // Only in[] exists
    checked = inArray.includes(initialValue);
  } else if (!inArray?.length && notInArray?.length) {
    // Only not-in[] exists
    checked = !notInArray.includes(initialValue);
  } else if (inArray?.length && notInArray?.length) {
    // Both exist: checked if in in[] and not in not-in[]
    checked =
      inArray.includes(initialValue) && !notInArray.includes(initialValue);
  } else {
    // Neither exists, fallback to initial value truthiness
    checked = Boolean(initialValue);
  }

  // Handle checkbox change
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = initialValue;
    if (e.target.checked) {
      // Add to in[], remove from not-in[]
      if (!inArray?.includes(value)) {
        formik.setFieldValue("arrayIn", [...(inArray || []), value]);
      }
      if (notInArray?.includes(value)) {
        formik.setFieldValue(
          "arrayNotIn",
          notInArray.filter((v) => v !== value),
        );
      }
    } else {
      // Add to not-in[], remove from in[]
      if (!notInArray?.includes(value)) {
        formik.setFieldValue("arrayNotIn", [...(notInArray || []), value]);
      }
      if (inArray?.includes(value)) {
        formik.setFieldValue(
          "arrayIn",
          inArray.filter((v) => v !== value),
        );
      }
    }
  };

  return (
    <input
      type="checkbox"
      className={`form-checkbox h-4 w-4 text-primary-600 ${className ?? ""}`}
      checked={checked}
      onChange={handleChange}
    />
  );
};

const PillarTableColumnCheckboxHeader = <T extends object = any>({
  cellValuePath,
  headingClassName,
  defaultCheckAll = false,
  ...props
}: PillarTableColumnCheckboxProps<T>) => {
  const formik = useFormikContext<FormikValues>();
  const { tableRowResults, loadedFirstTime } = usePillarTableContext<T, any>(); // grab _all_ rows
  const checkboxRef = useRef<HTMLInputElement>(null);

  // build an array of every row's "value" at that path
  const allValues = tableRowResults.map((row) =>
    lodashGet(row, cellValuePath!),
  );

  const inArray: any[] | undefined = formik.values.arrayIn;
  const notInArray: any[] | undefined = formik.values.arrayNotIn;

  const isDefaultChecked = !inArray && !notInArray && defaultCheckAll;

  const resolveCheckedState = useCallback(
    (currentChecked: boolean) => {
      let checked = currentChecked;
      if (!loadedFirstTime && defaultCheckAll) {
        checked = false;
      } else if (isDefaultChecked) {
        checked = true;
      } else {
        checked = false;
      }

      return checked;
    },
    [loadedFirstTime, isDefaultChecked, defaultCheckAll],
  );

  // detect "all checked" / "all unchecked" / "partial"
  const allChecked = inArray?.length === allValues.length || isDefaultChecked;
  const noneChecked = notInArray?.length === allValues.length;
  const indeterminate =
    !allChecked &&
    !noneChecked &&
    ((inArray?.length || 0) > 0 || (notInArray?.length || 0) > 0);
  const checked = resolveCheckedState(allChecked);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // → Check everything
      const { arrayIn, arrayNotIn, ...restFields } = formik.values;
      formik.setValues(restFields);
    } else {
      // → Uncheck everything
      formik.setFieldValue("arrayIn", []);
      formik.setFieldValue("arrayNotIn", []);
    }
  };

  return (
    <input
      type="checkbox"
      className={`form-checkbox h-4 w-4 text-primary-600 ${
        headingClassName ?? ""
      }`}
      checked={checked}
      onChange={handleChange}
      aria-checked={indeterminate ? "mixed" : checked}
    />
  );
};

PillarTableColumnCheckbox.HeaderComponent = PillarTableColumnCheckboxHeader;

export default PillarTableColumnCheckbox;
